import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, timer } from 'rxjs';
import { delay, take, tap } from 'rxjs/operators';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { BackupSeparator, OtxGenericInput, SetupInfo } from '../../../../app/@store';

import { AppInsightsMonitoringService } from 'src/app/shared/services/app-insights/app-insights-monitoring.service';
import { APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP } from 'src/app/shared/services/app-insights/events/remote-diagnostics.events';
import { LogLevelEnum } from 'src/app/shared/services/app-insights/app-insights-monitoring.service';

import { ClientConfiguration, ClientService, CLIENT_CONFIG } from '../../client.service';
import { InterceptorSkipHeader } from '../operational-data/operational-data.constants';

import { RemoteDiagnosticsHelpersService } from './remote-diagnostics-helpers.service';
import { DiagnosticsStackDeviceMainStructure } from './remote-diagnostics-topology.model';
import {
  DiagnosticsStackGenericResponse,
  DiagStackAvailableOTXSequence,
  DiagStackGenericResponse,
  DiagStackNode,
  RemoteDiagnosticsCloudApiContract,
  RemoteDiagnosticsGetVddListByComChannelGeneric,
  RemoteDiagnosticsSeesionGuid,
  RemoteDiagnosticsSession,
  RemoteDiagnosticsSessionClosed,
  RemoteDiagnosticsSuccessResponseData,
  RemoteDiagnosticsVciAvailable,
  RemoteDiagnosticsVciRegistration,
  RemoteDiagnosticsVddRootMetadataResponse,
  RemoteDiagnosticsVddRootStructureResponse,
  UseCases,
  UseCaseVddList,
} from './remote-diagnostics.model';

@Injectable()
export class RemoteDiagnosticsCloudApi implements RemoteDiagnosticsCloudApiContract {
  readonly isItMockedService = false;
  private readonly diagStackPoolManager: string;
  private diagStackWebsocketConnectionMessagesDelay = 500; // ms
  private readonly protocol = 'https://';

  private readonly sharedHeaders = new HttpHeaders({
    [InterceptorSkipHeader]: '',
  });

  constructor(
    @Inject(CLIENT_CONFIG) private clientConfiguration: ClientConfiguration,
    private clientService: ClientService,
    private remoteDiagnosticsHelpersService: RemoteDiagnosticsHelpersService,
    private appInsights: AppInsightsMonitoringService
  ) {
    this.diagStackPoolManager = this.clientConfiguration.diagStackPoolManager;
  }

  getDiagStackNodeNext(): Observable<DiagStackNode> {
    const endpoint = '/v1/nodes/next';

    this.appInsights.logEvent(
      APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.GET_FREE_DIAGSTACK_INSTANCE_ATTEMPT,
      LogLevelEnum.TRACE
    );

    return this.clientService.rawGet<DiagStackNode>(
      this.diagStackPoolManager + endpoint,
      null,
      null
    );
  }

  openDiagStackConnection(
    gatewaySerial: string,
    reason: string
  ): Observable<RemoteDiagnosticsSeesionGuid> {
    const endpoint = 'iot/v1/remote-diagnostics/session/' + gatewaySerial;
    reason = reason ?? 'Maintainer session';
    const body = {
      reason,
    };

    return this.clientService.post(endpoint, body);
  }

  closeDiagStackConnection(
    gatewaySerial: string,
    sessionGuid: string
  ): Observable<RemoteDiagnosticsSessionClosed> {
    const endpoint = 'iot/v1/remote-diagnostics/session/' + gatewaySerial;
    const body = {
      guid: sessionGuid,
    };

    return this.clientService.delete(endpoint, null, body);
  }

  userLogin(hostAndPath: string, oAuthToken: string): Observable<RemoteDiagnosticsSession> {
    const endpoint = '/user/login';
    const headers = this.sharedHeaders.append('Authorization', `Bearer ` + oAuthToken);

    return this.clientService.rawGet(this.protocol + hostAndPath + endpoint, null, headers);
  }

  userLoginCSRF(hostAndPath: string, csrfToken: string): Observable<RemoteDiagnosticsSession> {
    const endpoint = '/user/login';
    const headers = this.sharedHeaders.append('Authorization', `CSRF ` + csrfToken);

    this.appInsights.logEvent(
      APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.LOGIN_ATTEMPT,
      LogLevelEnum.TRACE
    );

    return this.clientService.rawGet(this.protocol + hostAndPath + endpoint, null, headers);
  }

  setupInfo(hostAndPath: string, xAuthToken: string): Observable<SetupInfo> {
    const endpoint = '/diagnostic-service/setup-info';
    const headers = this.sharedHeaders.append('x-auth-token', xAuthToken);

    return this.clientService.rawGet(this.protocol + hostAndPath + endpoint, null, headers);
  }

  userLogout(hostAndPath: string, xAuthToken: string): Observable<string> {
    const endpoint = '/user/logout';
    const headers = this.sharedHeaders.append('x-auth-token', xAuthToken);

    return this.clientService.rawDeleteText(this.protocol + hostAndPath + endpoint, null, headers);
  }

  remoteVciRegister(
    hostAndPath: string,
    xAuthToken: string,
    technicianWsUrl: string
  ): Observable<RemoteDiagnosticsVciRegistration> {
    const endpoint = '/remote-vci/register';
    const body = {
      technicianWsUrl,
    };
    const headers = this.sharedHeaders.append('x-auth-token', xAuthToken);

    this.appInsights.logEvent(
      APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.REGISTER_REMOTE_VCI_ATTEMPT,
      LogLevelEnum.TRACE
    );

    return this.clientService.rawPost(this.protocol + hostAndPath + endpoint, body, null, headers);
  }

  diagnosticServiceAvailableVci(
    hostAndPath: string,
    xAuthToken: string
  ): Observable<RemoteDiagnosticsVciAvailable[]> {
    const endpoint = '/diagnostic-service/vci/available';
    const headers = this.sharedHeaders.append('x-auth-token', xAuthToken);

    return this.clientService.rawGet(this.protocol + hostAndPath + endpoint, null, headers);
  }

  diagnosticServiceConnectVci(
    hostAndPath: string,
    xAuthToken: string,
    vciName?: string,
    resource?: string
  ): Observable<string> {
    const endpoint = '/diagnostic-service/vci/connect/' + vciName;
    const headers = this.sharedHeaders.append('x-auth-token', xAuthToken);
    const httpParams = new HttpParams().append('resource', resource);

    this.appInsights.logEvent(
      APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.REMOTE_VCI_CONNECT_ATTEMPT,
      LogLevelEnum.TRACE
    );

    return this.clientService.rawPostText(
      this.protocol + hostAndPath + endpoint,
      null,
      httpParams,
      headers
    );
  }

  diagnosticServiceLogicalLinkOpen(
    hostAndPath: string,
    xAuthToken: string,
    name: string,
    vciName: string,
    resource: string,
    sendTesterPresent: boolean = false,
    appInsightsLogText?: string
  ): Observable<string> {
    const endpoint = '/diagnostic-service/logical-link/open';
    const headers = this.sharedHeaders.append('x-auth-token', xAuthToken);

    const httpParams = new HttpParams()
      .append('name', name)
      .append('resource', resource)
      .append('vci', vciName)
      .append('sendTesterPresent', sendTesterPresent.toString());

    if (appInsightsLogText) {
      this.appInsights.logEvent(appInsightsLogText, LogLevelEnum.TRACE);
    }

    return this.clientService.rawPostText(
      this.protocol + hostAndPath + endpoint,
      null,
      httpParams,
      headers
    );
  }

  diagnosticServiceLogicalLinkClose(
    hostAndPath: string,
    xAuthToken: string,
    name: string,
    vciName: string,
    resource: string
  ): Observable<string> {
    const endpoint = '/diagnostic-service/logical-link/close/' + name;
    const headers = this.sharedHeaders.append('x-auth-token', xAuthToken);

    const httpParams = new HttpParams().append('resource', resource).append('vci', vciName);

    this.appInsights.logEvent(
      APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.MAIN_LOGICAL_LINK_CLOSE_ATTEMPT,
      LogLevelEnum.TRACE
    );

    return this.clientService.rawPostText(
      this.protocol + hostAndPath + endpoint,
      null,
      httpParams,
      headers
    );
  }

  diagnosticServiceForwardLogicalLinkOpen(
    hostAndPath: string,
    xAuthToken: string,
    logicalLinkName: string,
    mainLogicalLinkName: string,
    vciName: string,
    resource: string,
    sendTesterPresent: boolean = false,
    ecuVariantName?: string,
    appInsightsLogText?: string
  ): Observable<string> {
    const endpoint = '/diagnostic-service/forward-logical-link/open';
    const headers = this.sharedHeaders.append('x-auth-token', xAuthToken);

    const httpParams = new HttpParams()
      .append('name', logicalLinkName)
      .append('resource', resource)
      .append('vci', vciName)
      .append('mainLogicalLinkName', mainLogicalLinkName)
      .append('sendTesterPresent', sendTesterPresent.toString());

    if (ecuVariantName) {
      httpParams.append('ecuVariantName', ecuVariantName);
    }
    if (appInsightsLogText) {
      this.appInsights.logEvent(appInsightsLogText, LogLevelEnum.TRACE);
    }

    return this.clientService.rawPostText(
      this.protocol + hostAndPath + endpoint,
      null,
      httpParams,
      headers
    );
  }

  diagnosticServiceForwardLogicalLinkClose(
    hostAndPath: string,
    xAuthToken: string,
    name: string,
    vciName: string,
    resource: string,
    mainLogicalLinkName: string
  ): Observable<string> {
    const endpoint = '/diagnostic-service/forward-logical-link/close/' + name;
    const headers = this.sharedHeaders.append('x-auth-token', xAuthToken);

    const httpParams = new HttpParams()
      .append('resource', resource)
      .append('vci', vciName)
      .append('mainLogicalLinkName', mainLogicalLinkName);

    this.appInsights.logEvent(
      APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.MAIN_LOGICAL_LINK_CLOSE_ATTEMPT,
      LogLevelEnum.TRACE
    );

    return this.clientService.rawPostText(
      this.protocol + hostAndPath + endpoint,
      null,
      httpParams,
      headers
    );
  }

  diagnosticServiceLogicalLinkList(hostAndPath: string, xAuthToken: string): Observable<string[]> {
    const endpoint = '/diagnostic-service/logical-link/list';
    const headers = this.sharedHeaders.append('x-auth-token', xAuthToken);

    return this.clientService.rawGet(this.protocol + hostAndPath + endpoint, null, headers);
  }

  diagnosticServiceDataRead(
    hostAndPath: string,
    xAuthToken: string,
    logicalLinkName: string,
    resource: string,
    vciName: string,
    vdd: string,
    cycle: number = 0
  ): Observable<string> {
    const endpoint = '/diagnostic-service/data/read-single';
    const headers = this.sharedHeaders.append('x-auth-token', xAuthToken);

    const httpParams = new HttpParams()
      .append('cycle', cycle.toString())
      .append('logicalLink', logicalLinkName)
      .append('resource', resource)
      .append('vci', vciName)
      .append('vdd', vdd);

    return this.clientService.rawGetText(
      this.protocol + hostAndPath + endpoint,
      httpParams,
      headers
    );
  }

  diagnosticServiceDataReadSync(
    hostAndPath: string,
    xAuthToken: string,
    logicalLinkName: string,
    resource: string,
    vciName: string,
    vddName: string
  ): Observable<RemoteDiagnosticsSuccessResponseData> {
    const endpoint = '/diagnostic-service/data/read-single-sync';
    const headers = this.sharedHeaders.append('x-auth-token', xAuthToken);
    vddName = BackupSeparator.removeBackupSeparator(vddName);

    const httpParams = new HttpParams()
      .append('logicalLink', logicalLinkName)
      .append('resource', resource)
      .append('vci', vciName)
      .append('vdd', vddName);

    return this.clientService.rawGet(this.protocol + hostAndPath + endpoint, httpParams, headers);
  }

  diagnosticServiceDataWrite(
    hostAndPath: string,
    xAuthToken: string,
    logicalLinkName: string,
    resource: string,
    vciName: string,
    isVddRead: boolean,
    flatteredStructure: any,
    singleReadDataParameter: any
  ): Observable<any> {
    const endpoint = '/diagnostic-service/data/write';
    const headers = this.sharedHeaders.append('x-auth-token', xAuthToken);

    const httpParams = new HttpParams()
      .append('logicalLink', logicalLinkName)
      .append('resource', resource)
      .append('vci', vciName);

    if (!isVddRead) {
      return this.clientService.rawPostText(
        this.protocol + hostAndPath + endpoint,
        flatteredStructure,
        httpParams,
        headers
      );
    } else
      return this.clientService.rawPostText(
        this.protocol + hostAndPath + endpoint,
        singleReadDataParameter,
        httpParams,
        headers
      );
  }

  diagnosticProductMatrix(
    hostAndPath: string,
    xAuthToken: string,
    logicalLinkName: string,
    resource: string,
    vciName: string
  ): Observable<any> {
    const endpoint = '/diagnostic-service/product-matrix';
    const headers = this.sharedHeaders.append('x-auth-token', xAuthToken);

    const httpParams = new HttpParams()
      .append('logicalLink', logicalLinkName)
      .append('resource', resource)
      .append('vci', vciName);

    return this.clientService.rawGet(this.protocol + hostAndPath + endpoint, httpParams, headers);
  }

  scanTopology(
    hostAndPath: string,
    xAuthToken: string,
    mainLinkName: string,
    resource: string,
    vciName: string
  ): Observable<DiagnosticsStackGenericResponse<DiagnosticsStackDeviceMainStructure[]>> {
    const endpoint = '/diagnostic-service/scan/topology';
    const headers = this.sharedHeaders.append('x-auth-token', xAuthToken);

    const httpParams = new HttpParams()
      .append('mainLinkName', mainLinkName)
      .append('resource', resource)
      .append('vci', vciName);

    this.appInsights.logEvent(
      APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.FETCH_TOPOLOGY_ATTEMPT,
      LogLevelEnum.TRACE
    );

    return this.clientService.rawGet(this.protocol + hostAndPath + endpoint, httpParams, headers);
  }

  vddList(
    hostAndPath: string,
    xAuthToken: string,
    logicalLink: string,
    vciName?: string,
    resource?: string
  ): Observable<RemoteDiagnosticsGetVddListByComChannelGeneric> {
    const endpoint = '/diagnostic-info/vdd/' + logicalLink + '/list';
    const headers = this.sharedHeaders.append('x-auth-token', xAuthToken);
    const httpParams = new HttpParams().append('resource', resource).append('vci', vciName);

    return this.clientService.rawGet(this.protocol + hostAndPath + endpoint, httpParams, headers);
  }

  diagnosticInfoVddMetadata(
    hostAndPath: string,
    xAuthToken: string,
    resource: string,
    vciName: string,
    vddName: string,
    logicalLink: string
  ): Observable<RemoteDiagnosticsVddRootMetadataResponse> {
    vddName = BackupSeparator.removeBackupSeparator(vddName);
    const endpoint = `/diagnostic-info/vdd/${vddName}/metadatas`;
    const headers = this.sharedHeaders.append('x-auth-token', xAuthToken);

    const httpParams = new HttpParams()
      .append('baseVariant', logicalLink)
      .append('resource', resource)
      .append('vci', vciName);

    return this.clientService.rawGet(this.protocol + hostAndPath + endpoint, httpParams, headers);
  }

  diagnosticInfoVddStructure(
    hostAndPath: string,
    xAuthToken: string,
    resource: string,
    vciName: string,
    vddName: string,
    logicalLink: string
  ): Observable<DiagnosticsStackGenericResponse<RemoteDiagnosticsVddRootStructureResponse[]>> {
    vddName = BackupSeparator.removeBackupSeparator(vddName);
    const endpoint = `/diagnostic-info/vdd/${vddName}/structure`;
    const headers = this.sharedHeaders.append('x-auth-token', xAuthToken);

    const httpParams = new HttpParams()
      .append('baseVariant', logicalLink)
      .append('resource', resource)
      .append('vci', vciName);

    return this.clientService.rawGet(this.protocol + hostAndPath + endpoint, httpParams, headers);
  }

  diagnosticServiceCancelCycle(
    hostAndPath: string,
    xAuthToken: string,
    requestId: string
  ): Observable<string> {
    const endpoint = '/diagnostic-service/data/cancel-cycle/' + requestId;
    const headers = this.sharedHeaders.append('x-auth-token', xAuthToken);

    return this.clientService.rawPostText(
      this.protocol + hostAndPath + endpoint,
      null,
      null,
      headers
    );
  }

  diagnosticServiceDtcActive(
    hostAndPath: string,
    xAuthToken: string,
    logicalLinkName: string,
    vciName: string,
    resource: string
  ): Observable<DiagStackGenericResponse> {
    const endpoint = '/diagnostic-service/dtc/active';
    const headers = this.sharedHeaders.append('x-auth-token', xAuthToken);

    const httpParams = new HttpParams()
      .append('linkName', logicalLinkName)
      .append('resource', resource)
      .append('vci', vciName);

    this.appInsights.logEvent(
      APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.GET_ACTIVE_DTC_ATTEMPT,
      LogLevelEnum.TRACE
    );

    return this.clientService.rawGet(this.protocol + hostAndPath + endpoint, httpParams, headers);
  }

  diagnosticGenericOtxAvailableSequences(
    hostAndPath: string,
    xAuthToken: string
  ): Observable<DiagStackAvailableOTXSequence[]> {
    const endpoint = '/diagnostic-generic-otx/available-sequences';
    const headers = this.sharedHeaders.append('x-auth-token', xAuthToken);

    return this.clientService.rawGet(this.protocol + hostAndPath + endpoint, null, headers);
  }

  diagnosticGenericOtxExecute<T, R>(
    hostAndPath: string,
    xAuthToken: string,
    sequenceModel: OtxGenericInput<T>
  ): Observable<R> {
    // it is OtxGenericResponse underneath
    const endpoint = '/diagnostic-generic-otx/execute';
    const headers = this.sharedHeaders.append('x-auth-token', xAuthToken);

    return this.clientService.rawPostText(
      this.protocol + hostAndPath + endpoint,
      sequenceModel,
      null,
      headers
    );
  }

  diagnosticGenericOtxExecuteAsync<T>(
    hostAndPath: string,
    xAuthToken: string,
    sequenceModel: OtxGenericInput<T>
  ): Observable<string> {
    // it is OtxGenericResponse underneath
    const endpoint = '/diagnostic-generic-otx/execute-async';
    const headers = this.sharedHeaders.append('x-auth-token', xAuthToken);

    return this.clientService.rawPostText(
      this.protocol + hostAndPath + endpoint,
      sequenceModel,
      null,
      headers
    );
  }

  connectResponseWebSocketSubject(hostAndPath: string, xAuthToken: string): WebSocketSubject<any> {
    const uniquePart = this.remoteDiagnosticsHelpersService.makeUrlWebsocketPart();
    const url = 'wss://' + hostAndPath + `/viessmann/results/${uniquePart}/websocket`;
    const subject: WebSocketSubject<any> = webSocket({
      url,
      deserializer: (msg) => msg,
    });

    const connectObject = this.remoteDiagnosticsHelpersService.makeWebsocketFormat({
      CONNECT: {
        'x-auth-token': xAuthToken,
        'accept-version': '1.1,1.0',
        'heart-beat': '10000,10000',
      },
    });

    const subscriptionObject = this.remoteDiagnosticsHelpersService.makeWebsocketFormat({
      SUBSCRIBE: {
        id: 'sub-0',
        destination: '/topic/results',
      },
    });

    timer(this.diagStackWebsocketConnectionMessagesDelay)
      .pipe(
        take(1),
        tap(() => subject.next(connectObject)),
        delay(this.diagStackWebsocketConnectionMessagesDelay),
        tap(() => subject.next(subscriptionObject))
      )
      .subscribe();

    return subject;
  }

  disconnect(subject: WebSocketSubject<any>): void {
    const disconnectionObject = this.remoteDiagnosticsHelpersService.makeWebsocketFormat({
      DISCONNECT: {},
    });
    subject.next(disconnectionObject);
  }

  diagnosticInfoUseCases(
    hostAndPath: string,
    xAuthToken: string,
    logicalLink: string,
    vciName: string,
    resource: string,
    vinNumber: string
  ): Observable<UseCases> {
    const endpoint = '/diagnostic-info/available-use-cases/' + logicalLink;
    const headers = this.sharedHeaders.append('x-auth-token', xAuthToken);
    const httpParams = new HttpParams()
      .append('resource', resource)
      .append('vci', vciName)
      .append('vin', vinNumber);

    return this.clientService.rawGet(this.protocol + hostAndPath + endpoint, httpParams, headers);
  }

  diagnosticInfoUseCaseVddList(
    hostAndPath: string,
    xAuthToken: string,
    logicalLink: string,
    vciName: string,
    resource: string,
    vinNumber: string,
    useCase: string,
    useCaseType: string
  ): Observable<UseCaseVddList> {
    const endpoint = `/diagnostic-info/vdd/${logicalLink}/${useCase}/list`;
    const headers = this.sharedHeaders.append('x-auth-token', xAuthToken);
    const httpParams = new HttpParams()
      .append('resource', resource)
      .append('useCaseType', useCaseType)
      .append('vci', vciName)
      .append('vin', vinNumber);

    return this.clientService.rawGet(this.protocol + hostAndPath + endpoint, httpParams, headers);
  }
}
